import {WBIcon} from '@wandb/ui';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {GraphQLError} from 'graphql/error';
import {debounce} from 'lodash';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useAvailableOrgQuery,
  useAvailableTeamQuery,
  useCreateAcademicOrReverseTrialMutation,
  useCreateTeamMutation,
  useUserOrganizationsQuery,
} from '../../generated/graphql';
import {captureError} from '../../integrations';
import {PlanName} from '../../pages/Billing/util';
import {HeaderWithBottomMargin} from '../../pages/OnboardingFlow.styles';
import {useDispatch} from '../../state/hooks';
import {reloadCurrentViewer} from '../../state/viewer/actions';
import {useViewer} from '../../state/viewer/hooks';
import {
  getPrimarySub,
  isAcademicOrg,
  isAcademicTrialOrg,
  isNonAcademicOrg,
  isPersonalOrg,
} from '../../util/accounts/pricing';
import {
  doNotRetryContext,
  getErrMsgFromError,
  propagateErrorsContext,
} from '../../util/errors';
import {slugFormat} from '../../util/text';
import * as urls from '../../util/urls';
import {pricing, termsOfService} from '../../util/urls';
import {TextField} from '../Form/TextField';
import {AccountType} from '../Search/SearchNav/AccountSelectorContextProvider';
import {useStorageBucketConfig} from './hooks';
import StorageBucketInfoInput from './StorageBucketInfoInput';
import * as S from './styles';
import {StorageBucketInfoFormValue, TeamType} from './types';

const NEW_ORG_PLACEHOLDER_ID = 'NEW_ORG_PLACEHOLDER_ID';
const TEAM_LIMIT_MESSAGE = 'organization does not have any private teams left';

const TEAM_NAME_TAKEN_MESSAGE =
  'Organization name already taken. Please choose a different name.';

export type CreateTeamData = {
  teamEntityName: string;
  orgName: string;
  teamType: TeamType;
  storageBucketInfo?: StorageBucketInfoFormValue;
};

type RenderButtonsParams = {
  createTeam: () => Promise<CreateTeamData | undefined>;
  loading: boolean;
  disabled: boolean;
  formState?: {
    selectedTeamType: string | null;
    entityName: string | null;
    orgName: string | null;
    agreedToAcademicPolicy: boolean;
  };
};

type CreateTeamProps = {
  analyticsLocation: string;
  autoGenerateOrgName?: boolean;
  renderButtons?: (p: RenderButtonsParams) => ReactNode;
  onCreateTeam?: (d: CreateTeamData) => void;
  shouldHideTeamTypeSelection?: boolean;
  wbUsageType?: AccountType;
};

const CreateTeam: FC<CreateTeamProps> = React.memo(
  ({
    analyticsLocation,
    autoGenerateOrgName,
    renderButtons,
    onCreateTeam,
    shouldHideTeamTypeSelection,
    wbUsageType,
  }) => {
    useEffect(() => {
      window.analytics?.track('Create Team Viewed', {
        location: analyticsLocation,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dispatch = useDispatch();
    const viewer = useViewer();

    const userOrganizationsQueryResult = useUserOrganizationsQuery();
    const userOrganizations = useMemo(() => {
      return userOrganizationsQueryResult?.data?.viewer?.organizations ?? [];
    }, [userOrganizationsQueryResult?.data]);

    // 'Academic' uses trial team
    // 'Work' uses trial team unless a user specifies an existing org
    const [createAcademicOrReverseTrial] =
      useCreateAcademicOrReverseTrialMutation({
        context: {...propagateErrorsContext(), ...doNotRetryContext()},
      });
    const [createTeamMutation] = useCreateTeamMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [extraInfoMsg, setExtraInfoMsg] = useState<JSX.Element | null>(null);
    const [selectedTeamType, setSelectedTeamType] = useState<TeamType>(
      wbUsageType === AccountType.Academic ? 'academic' : 'work'
    );

    const [entityName, setEntityName] = useState<string | null>(null);
    const [orgName, setOrgName] = useState<string | null>(null);

    const [agreedToAcademicPolicy, setAgreedToAcademicPolicy] =
      useState<boolean>(shouldHideTeamTypeSelection ?? false);
    const [creatingTeam, setCreatingTeam] = useState(false);

    const entityNameFilled = !!entityName;
    const orgNameFilled = !!orgName;

    const [isTyping, setIsTyping] = useState(false);

    const debounceIsTyping = useMemo(
      () => debounce(() => setIsTyping(false), 300),
      []
    );

    const updateEntityName = useCallback(
      (name: string) => {
        setEntityName(slugFormat(name));
        setIsTyping(true);
        debounceIsTyping();
      },
      [debounceIsTyping]
    );

    const updateOrgName = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => setOrgName(e.target.value),
      []
    );

    useEffect(() => {
      if (autoGenerateOrgName && entityName) {
        setOrgName(`${entityName}-org`);
      }
    }, [autoGenerateOrgName, entityName]);

    const {
      loading: loadingAvailability,
      isEntityAvailable,
      isOrgAvailable,
    } = useAvailability({
      entityName,
      orgName,
      skip: isTyping,
    });
    const isTeamNameAvailable = isEntityAvailable && isOrgAvailable;

    useEffect(() => {
      if (!loadingAvailability && entityNameFilled && !isTeamNameAvailable) {
        setErrorMsg(TEAM_NAME_TAKEN_MESSAGE);
        return;
      }
      setErrorMsg(null);
    }, [
      selectedTeamType,
      entityNameFilled,
      isEntityAvailable,
      orgNameFilled,
      isOrgAvailable,
      loadingAvailability,
      isTeamNameAvailable,
    ]);

    const [selectedOrgID, setSelectedOrgID] = useState<string | null>(null);
    const creatingNewOrg =
      selectedOrgID == null || selectedOrgID === NEW_ORG_PLACEHOLDER_ID;

    const selectedUserOrg = useMemo(
      () => userOrganizations.find(o => o.id === selectedOrgID) ?? null,
      [selectedOrgID, userOrganizations]
    );

    const orgOptions = useMemo(() => {
      // Filter out the personal and academic organizations
      const userOrgOptions = userOrganizations
        .filter(o => !isPersonalOrg(o) && isNonAcademicOrg(o))
        .map(o => ({
          text: o.name,
          value: o.id,
        }));

      return [
        ...userOrgOptions,
        {
          text: 'Create new organization',
          value: NEW_ORG_PLACEHOLDER_ID,
        },
      ];
    }, [userOrganizations]);
    const existingOrgOptionAvailable = orgOptions.some(
      o => o.value !== NEW_ORG_PLACEHOLDER_ID
    );

    // Set the first org as the default selection when it's available
    useEffect(() => {
      const option = orgOptions.find(
        ({value}) => value !== NEW_ORG_PLACEHOLDER_ID
      );
      if (option != null) {
        setSelectedOrgID(option.value);
      }
    }, [orgOptions]);

    const viewerNotAdminOfSelectedOrg =
      selectedUserOrg != null &&
      viewer != null &&
      !selectedUserOrg.members.some(m => m.id === viewer.id && m.admin);

    const academicTeamLink: ReactNode = useMemo(() => {
      const teamName = userOrganizations.find(
        o => (isAcademicOrg(o) || isAcademicTrialOrg(o)) && o.teams.length > 0
      )?.teams[0].name;

      if (teamName == null) {
        return null;
      }

      return [
        <TargetBlank key={0} href={`/${teamName}`}>
          {teamName}
        </TargetBlank>,
      ];
    }, [userOrganizations]);

    const orgsWithNoRemainingTeamSlotsMap: Map<string, ReactNode[]> =
      useMemo(() => {
        const orgsWithNoRemainingTeamSlots = userOrganizations.filter(o => {
          const maxTeams: number =
            o.subscriptions[0]?.privileges?.num_private_teams ?? 1;

          const teamCount = o.teams.length;
          return teamCount >= maxTeams;
        });
        return new Map(
          orgsWithNoRemainingTeamSlots.map(o => [
            o.id,
            o.teams.map((t, i) => (
              <React.Fragment key={t.id}>
                {i > 0 && `, `}
                <TargetBlank href={`/${t.name}`}>{t.name}</TargetBlank>
              </React.Fragment>
            )),
          ])
        );
      }, [userOrganizations]);

    const noRemainingTeamSlotsTeamLinks =
      selectedOrgID != null
        ? orgsWithNoRemainingTeamSlotsMap.get(selectedOrgID) ?? null
        : null;

    useEffect(() => {
      const noTeamsLeft =
        (selectedTeamType === 'academic' && academicTeamLink != null) ||
        (selectedTeamType === 'work' && noRemainingTeamSlotsTeamLinks != null);
      if (noTeamsLeft) {
        window.analytics?.track(`No teams left viewed`, {
          teamType: selectedTeamType,
          location: analyticsLocation,
        });
      }
    }, [
      selectedTeamType,
      academicTeamLink,
      noRemainingTeamSlotsTeamLinks,
      analyticsLocation,
    ]);
    const onNoTeamsLeftClick = useCallback(() => {
      window.analytics?.track(`No teams left clicked`, {
        teamType: selectedTeamType,
        location: analyticsLocation,
      });
    }, [selectedTeamType, analyticsLocation]);

    const submittedOrgName = useMemo(() => {
      if (orgNameFilled) {
        return orgName;
      }

      const selectedOption = orgOptions.find(
        opt => opt.value === selectedOrgID
      );
      return selectedOption?.text ?? null;
    }, [orgNameFilled, orgName, orgOptions, selectedOrgID]);

    const {
      usingExternalStorage,
      setUsingExternalStorage,
      isExternalStorageEnabled,
      bucketInfo,
      setBucketInfo,
      getInitialBucketInfo,
      isValidState,
      storageBucketInfoOptions,
      cloudProviderOptions,
      formIsReady: formIsReadyProp,
      setOrganizationId,
      setIsExternalStorageEnabled,
      resetBucketInfo,
    } = useStorageBucketConfig();

    useEffect(() => {
      setOrganizationId(creatingNewOrg ? null : selectedOrgID);
      setUsingExternalStorage(false);
    }, [
      selectedOrgID,
      creatingNewOrg,
      setOrganizationId,
      setUsingExternalStorage,
    ]);

    useEffect(() => {
      if (selectedUserOrg == null) {
        setIsExternalStorageEnabled(false);
        setUsingExternalStorage(false);
        return;
      }
      const primarySub = getPrimarySub(selectedUserOrg);
      setIsExternalStorageEnabled(
        primarySub?.plan?.name === PlanName.Enterprise
      );
    }, [selectedUserOrg, setIsExternalStorageEnabled, setUsingExternalStorage]);
    const [displayStorageOptions, setDisplayStorageOptions] = useState(false);
    useEffect(() => {
      if (selectedUserOrg == null || selectedTeamType === 'academic') {
        setDisplayStorageOptions(false);
        return;
      }
      setDisplayStorageOptions(
        !isPersonalOrg(selectedUserOrg) && isNonAcademicOrg(selectedUserOrg)
      );
    }, [selectedUserOrg, selectedTeamType]);

    const onClickAcademic = useCallback(() => {
      window.analytics?.track('Team Type Selected', {
        location: analyticsLocation,
        teamType: 'academic',
      });
      setSelectedTeamType('academic');
    }, [analyticsLocation]);
    const onClickWork = useCallback(() => {
      window.analytics?.track('Team Type Selected', {
        location: analyticsLocation,
        teamType: 'work',
      });
      setSelectedTeamType('work');
    }, [analyticsLocation]);

    const createAcademicTeam = useCallback(async () => {
      try {
        const res = await createAcademicOrReverseTrial({
          variables: {
            // for academic teams, we use the specified team name as the org name
            newOrganizationName: orgName ?? '',
            newTeamName: entityName ?? '',
            isAcademic: true,
          },
        });

        // Handle possible graphql errors. This is less common than the exception case below,
        // but can, in theory, still happen.
        if (res.errors != null) {
          handleGQLErrors({
            analyticsLocation,
            errors: res.errors,
            errLabel: 'CreateTeam-submitAcademicTeam',
            errExtra: {orgName, entityName},
            trackingEventName: `Create Team Error Viewed`,
            trackingOrgName: submittedOrgName,
            setErrorMsg,
          });
          return;
        }

        window.analytics?.track('Trial Team Submitted', {
          organizationName: submittedOrgName,
          entityName,
          location: analyticsLocation,
        });
      } catch (err) {
        handleMutationError({
          analyticsLocation,
          err,
          errLabel: 'CreateTeam-submitAcademicTeam',
          errExtra: {orgName: entityName, entityName},
          trackingEventName: `Create Team Error Viewed`,
          trackingOrgName: submittedOrgName,
          setErrorMsg,
          setExtraInfoMsg,
        });
        throw err;
      }

      setErrorMsg(null);
    }, [
      orgName,
      entityName,
      createAcademicOrReverseTrial,
      submittedOrgName,
      analyticsLocation,
    ]);

    const createReverseTrialTeam = useCallback(async () => {
      try {
        const res = await createAcademicOrReverseTrial({
          variables: {
            newOrganizationName: orgName ?? '',
            newTeamName: entityName ?? '',
            isAcademic: false,
          },
        });

        // Handle possible graphql errors. This is less common than the exception case below,
        // but can, in theory, still happen.
        if (res.errors != null) {
          handleGQLErrors({
            analyticsLocation,
            errors: res.errors,
            errLabel: 'CreateTeam-submitWorkTrialTeam',
            errExtra: {orgName, entityName},
            trackingEventName: `Create Team Error Viewed`,
            trackingOrgName: submittedOrgName,
            setErrorMsg,
          });
          return;
        }
        window.analytics?.track('Trial Team Submitted', {
          organizationName: submittedOrgName,
          entityName,
          location: analyticsLocation,
        });
      } catch (err) {
        handleMutationError({
          analyticsLocation,
          err,
          errLabel: 'CreateTeam-submitWorkTrialTeam',
          errExtra: {orgName, entityName},
          trackingEventName: `Create Team Error Viewed`,
          trackingOrgName: submittedOrgName,
          setErrorMsg,
          setExtraInfoMsg,
        });
        throw err;
      }

      setErrorMsg(null);
    }, [
      entityName,
      orgName,
      createAcademicOrReverseTrial,
      submittedOrgName,
      analyticsLocation,
    ]);

    const createWorkTeam = useCallback(async () => {
      try {
        const res = await createTeamMutation({
          variables: {
            teamName: entityName ?? '',
            organizationId: selectedOrgID ?? '',
            storageBucketInfo: bucketInfo,
          },
        });

        // Handle possible graphql errors. This is less common than the exception case below,
        // but can, in theory, still happen.
        if (res.errors != null) {
          handleGQLErrors({
            analyticsLocation,
            errors: res.errors,
            errLabel: 'CreateTeam-submitTeam',
            errExtra: {orgName: submittedOrgName, entityName},
            trackingEventName: `Create Team Error Viewed`,
            trackingOrgName: submittedOrgName,
            setErrorMsg,
          });
          return;
        }

        window.analytics?.track('Team Submitted', {
          organizationName: submittedOrgName,
          entityName,
          location: analyticsLocation,
        });
      } catch (err) {
        handleMutationError({
          analyticsLocation,
          err,
          errLabel: 'CreateTeam-submitTeam',
          errExtra: {orgName: submittedOrgName, entityName},
          trackingEventName: `Create Team Error Viewed`,
          trackingOrgName: submittedOrgName,
          setErrorMsg,
          setExtraInfoMsg,
        });
        throw err;
      }

      setErrorMsg(null);
    }, [
      entityName,
      selectedOrgID,
      submittedOrgName,
      bucketInfo,
      createTeamMutation,
      analyticsLocation,
    ]);

    const createTeamDisabled =
      isTyping ||
      loadingAvailability ||
      !entityNameFilled ||
      (!loadingAvailability && !isEntityAvailable) ||
      (selectedTeamType === 'work' &&
        creatingNewOrg &&
        (!orgNameFilled || (!loadingAvailability && !isOrgAvailable))) ||
      (selectedTeamType === 'work' &&
        !creatingNewOrg &&
        (noRemainingTeamSlotsTeamLinks != null ||
          viewerNotAdminOfSelectedOrg)) ||
      (selectedTeamType === 'academic' &&
        ((!loadingAvailability && !isTeamNameAvailable) ||
          !agreedToAcademicPolicy ||
          academicTeamLink != null));

    const [isStorageValid, setIsStorageValid] = useState(true);
    useEffect(() => {
      if (!usingExternalStorage) {
        setIsStorageValid(true);
        return;
      }
      setIsStorageValid(isValidState.state === 'valid');
    }, [usingExternalStorage, isValidState]);

    const formState = {
      selectedTeamType,
      entityName,
      orgName,
      agreedToAcademicPolicy,
      bucketInfo,
    };

    const onClickCreateTeam = useCallback(async () => {
      window.analytics?.track(`Create Team Clicked`, {
        teamType: selectedTeamType,
        location: analyticsLocation,
      });

      if (creatingTeam) {
        return;
      }
      if (!entityNameFilled || !entityName) {
        setErrorMsg('You must set a team name');
        return;
      }
      if (!isEntityAvailable || !isTeamNameAvailable) {
        setErrorMsg(TEAM_NAME_TAKEN_MESSAGE);
        return;
      }

      setCreatingTeam(true);

      try {
        // create the appropriate team type
        if (selectedTeamType === 'academic') {
          await createAcademicTeam();
        } else if (selectedTeamType === 'work' && !creatingNewOrg) {
          await createWorkTeam();
        } else if (selectedTeamType === 'work' && orgNameFilled) {
          await createReverseTrialTeam();
        } else {
          setErrorMsg('Error creating team');
          return;
        }

        // Update viewer in redux state with newly created team
        dispatch(reloadCurrentViewer());

        const createTeamData: CreateTeamData = {
          teamEntityName: entityName,
          orgName: submittedOrgName ?? '',
          teamType: selectedTeamType,
          storageBucketInfo: bucketInfo,
        };
        onCreateTeam?.(createTeamData);
        return createTeamData;
      } catch (err) {
        captureError(err, 'CreateTeam-createTeam');
        return;
      } finally {
        setCreatingTeam(false);
      }
    }, [
      isTeamNameAvailable,
      selectedTeamType,
      analyticsLocation,
      creatingTeam,
      entityNameFilled,
      entityName,
      isEntityAvailable,
      creatingNewOrg,
      orgNameFilled,
      dispatch,
      submittedOrgName,
      onCreateTeam,
      createAcademicTeam,
      createWorkTeam,
      createReverseTrialTeam,
      bucketInfo,
    ]);

    const isEntityUnavailable =
      entityNameFilled && !loadingAvailability && !isTeamNameAvailable;

    let errorComponent = null;
    if (selectedTeamType === 'academic') {
      errorComponent = errorMsg ? (
        <S.ErrorMessage>{errorMsg}</S.ErrorMessage>
      ) : academicTeamLink != null ? (
        <S.ExtraInfoMessage>
          You are already part of a free academic team, {academicTeamLink}. You
          can create a work team or delete your academic team by{' '}
          <S.ContactSalesLink
            href={urls.contact()}
            onClick={onNoTeamsLeftClick}>
            contacting support <S.ContactSalesIcon name="right-arrow" />
          </S.ContactSalesLink>
        </S.ExtraInfoMessage>
      ) : null;
    }
    if (selectedTeamType === 'work') {
      errorComponent = errorMsg ? (
        <S.ErrorMessage>{errorMsg}</S.ErrorMessage>
      ) : noRemainingTeamSlotsTeamLinks != null ? (
        <S.ExtraInfoMessage>
          Your organization {selectedUserOrg?.name ?? ''} already has{' '}
          {noRemainingTeamSlotsTeamLinks.length} team(s) (
          {noRemainingTeamSlotsTeamLinks}), and on the{' '}
          {selectedUserOrg?.subscriptions[0]?.plan.displayName ?? ''} plan you
          can only create{' '}
          {selectedUserOrg?.subscriptions[0]?.privileges?.num_private_teams ??
            1}{' '}
          team(s). To unlock more teams,{' '}
          <S.ContactSalesLink
            href={urls.contactSalesPricing()}
            onClick={onNoTeamsLeftClick}>
            contact sales <S.ContactSalesIcon name="right-arrow" />
          </S.ContactSalesLink>
        </S.ExtraInfoMessage>
      ) : viewerNotAdminOfSelectedOrg ? (
        <S.ExtraInfoMessage>
          Please ask your organization administrator to create a new team.
        </S.ExtraInfoMessage>
      ) : null;
    }

    return (
      <>
        <HeaderWithBottomMargin>Organization URL</HeaderWithBottomMargin>
        <TextField
          value={entityName ?? ''}
          prefix="wandb.ai/"
          size="large"
          onChange={updateEntityName}
          placeholder="organization-name"
          errorState={isEntityUnavailable || errorMsg !== null}
          dataTest="team-name-input"
        />
        {isEntityUnavailable && <S.EntityInputIcon name="close" />}
        {errorComponent}
        <S.WarningMessage>
          Choose carefully, this cannot be changed.
        </S.WarningMessage>
        {!shouldHideTeamTypeSelection && (
          <S.InputRadio>
            Choose team type:
            <S.Tooltip
              position="top center"
              trigger={
                <S.InputRadioOption
                  active={selectedTeamType === 'work'}
                  onClick={onClickWork}>
                  {selectedTeamType === 'work' && <S.RadioIcon name="check" />}
                  Work
                </S.InputRadioOption>
              }>
              For users at companies, collaborating on ML projects at work
            </S.Tooltip>
            <S.Tooltip
              position="top center"
              trigger={
                <S.InputRadioOption
                  active={selectedTeamType === 'academic'}
                  onClick={onClickAcademic}>
                  {selectedTeamType === 'academic' && (
                    <S.RadioIcon name="check" />
                  )}
                  Academic
                </S.InputRadioOption>
              }>
              For academic research and projects outside of corporate use.
            </S.Tooltip>
          </S.InputRadio>
        )}
        {selectedTeamType === 'academic' ? (
          <>
            {academicTeamLink == null && !shouldHideTeamTypeSelection && (
              <>
                <S.AgreeCheckbox
                  label={
                    <label>
                      I understand that academic and open-source teams are only
                      available for non-corporate use.{' '}
                      <TargetBlank
                        href={termsOfService()}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        See Terms of Service
                      </TargetBlank>
                    </label>
                  }
                  checked={agreedToAcademicPolicy}
                  onChange={() => setAgreedToAcademicPolicy(prev => !prev)}
                />
              </>
            )}
            {renderButtons?.({
              createTeam: onClickCreateTeam,
              loading: creatingTeam,
              disabled: creatingTeam || createTeamDisabled || !isStorageValid,
              formState,
            }) ?? (
              <S.Button
                primary
                onClick={onClickCreateTeam}
                loading={creatingTeam}
                disabled={
                  creatingTeam || createTeamDisabled || !isStorageValid
                }>
                Create Team
              </S.Button>
            )}
            {extraInfoMsg && (
              <S.ExtraInfoMessage>{extraInfoMsg}</S.ExtraInfoMessage>
            )}
          </>
        ) : selectedTeamType === 'work' ? (
          <>
            {(!autoGenerateOrgName || existingOrgOptionAvailable) && (
              <S.InputLabelContainer>
                <S.InputLabel>Company / Organization</S.InputLabel>

                {creatingNewOrg && (
                  <Tooltip
                    content={
                      <>
                        Your team will be on the Free Trial plan. After creating
                        your team, you can upgrade to the Starter or Enterprise
                        plan on the Subscriptions page.{' '}
                        <TargetBlank href={pricing()}>See Pricing</TargetBlank>
                      </>
                    }
                    onClick={() => {
                      window.analytics?.track('Org Tooltip Clicked', {
                        entityName,
                        organizationName: submittedOrgName,
                      });
                    }}
                  />
                )}
              </S.InputLabelContainer>
            )}
            {existingOrgOptionAvailable && (
              <S.InputDropdown
                selection
                value={selectedOrgID ?? ''}
                onChange={(e, d) => {
                  setSelectedOrgID(d.value as string);
                }}
                options={orgOptions}
                data-test="organization-dropdown"
              />
            )}
            {creatingNewOrg && !autoGenerateOrgName && (
              <>
                <S.InputLabel>Name</S.InputLabel>
                <S.InputText
                  value={orgName ?? ''}
                  onChange={updateOrgName}
                  placeholder="Company name"
                  error={
                    orgNameFilled && !loadingAvailability && !isOrgAvailable
                  }
                />
              </>
            )}

            {displayStorageOptions && (
              <>
                <StorageBucketInfoInput
                  usingExternalStorage={usingExternalStorage}
                  setUsingExternalStorage={setUsingExternalStorage}
                  setIsExternalStorageEnabled={setIsExternalStorageEnabled}
                  isExternalStorageEnabled={isExternalStorageEnabled}
                  bucketInfo={bucketInfo}
                  setBucketInfo={setBucketInfo}
                  getInitialBucketInfo={getInitialBucketInfo}
                  isValidState={isValidState}
                  storageBucketInfoOptions={storageBucketInfoOptions}
                  cloudProviderOptions={cloudProviderOptions}
                  formIsReady={formIsReadyProp}
                  resetBucketInfo={resetBucketInfo}
                  setOrganizationId={setOrganizationId}
                  storageLabelOptions={
                    <>
                      <S.InputRadio>
                        Choose storage type:
                        <S.Tooltip
                          position="top center"
                          trigger={
                            <S.InputRadioOption
                              active={!usingExternalStorage}
                              onClick={() => setUsingExternalStorage(false)}>
                              {!usingExternalStorage && (
                                <S.RadioIcon name="check" />
                              )}
                              W&B Cloud Storage
                            </S.InputRadioOption>
                          }>
                          Store this team's data in W&B cloud storage
                        </S.Tooltip>
                        <S.Tooltip
                          position="top center"
                          trigger={
                            <S.InputRadioOption
                              data-test="external-storage-radio"
                              disabled={!isExternalStorageEnabled}
                              active={usingExternalStorage}
                              onClick={() => {
                                if (isExternalStorageEnabled) {
                                  setUsingExternalStorage(true);
                                }
                              }}>
                              {usingExternalStorage && (
                                <S.RadioIcon name="check" />
                              )}
                              External Storage
                            </S.InputRadioOption>
                          }>
                          Specify a bucket in another cloud provider to use as
                          storage
                          {!isExternalStorageEnabled && (
                            <>
                              <br />
                              <br />
                              <strong>
                                External storage is not available with your
                                current subscription plan. To enable this
                                feature, please contact{' '}
                                <a href="mailto:sales@wandb.com">
                                  sales@wandb.com
                                </a>
                                .
                              </strong>
                            </>
                          )}
                        </S.Tooltip>
                      </S.InputRadio>
                      <S.WarningMessage>
                        Storage settings cannot be changed after team
                        creation.&nbsp;
                        <S.SeeDocsLink
                          href={`https://docs.wandb.ai/guides/app/features/teams#secure-storage-connector`}>
                          See docs
                          <WBIcon name="right-arrow" />
                        </S.SeeDocsLink>
                      </S.WarningMessage>
                    </>
                  }
                />
              </>
            )}

            {renderButtons?.({
              createTeam: onClickCreateTeam,
              loading: creatingTeam,
              disabled: creatingTeam || createTeamDisabled || !isStorageValid,
              formState,
            }) ?? (
              <S.Button
                primary
                data-test="submit-create-team"
                onClick={onClickCreateTeam}
                loading={creatingTeam}
                disabled={
                  creatingTeam || createTeamDisabled || !isStorageValid
                }>
                Create Team
              </S.Button>
            )}
            {extraInfoMsg && (
              <S.ExtraInfoMessage>{extraInfoMsg}</S.ExtraInfoMessage>
            )}
          </>
        ) : null}
      </>
    );
  }
);

export default CreateTeam;

type TooltipProps = {
  content: React.ReactElement;
  onClick?: () => void;
};

const Tooltip: FC<TooltipProps> = React.memo(({content, onClick}) => {
  return (
    <S.Tooltip
      position="top center"
      on="click"
      content={content}
      trigger={<S.TooltipIcon name="help-circle-outline" onClick={onClick} />}
    />
  );
});

type HandleMutationErrorProps = {
  analyticsLocation: string;
  err: any;
  errLabel: string;
  errExtra?: {[key: string]: any};
  trackingEventName: string;
  trackingOrgName: string | null;
  setErrorMsg: (m: string | null) => void;
  setExtraInfoMsg: (m: JSX.Element | null) => void;
};

function handleMutationError({
  analyticsLocation,
  err,
  errLabel,
  errExtra,
  trackingEventName,
  trackingOrgName,
  setErrorMsg,
  setExtraInfoMsg,
}: HandleMutationErrorProps) {
  captureError(err, errLabel, {extra: errExtra});
  const errMsg = getErrMsgFromError(err);
  if (errMsg === TEAM_LIMIT_MESSAGE) {
    const contactSalesMsg: JSX.Element = (
      <>
        Your organization does not have any private teams left.
        <br />
        Contact sales to upgrade your plan and create more teams.&nbsp;
        <TargetBlank href={urls.contactSalesPricing()}>
          Contact sales →
        </TargetBlank>
      </>
    );
    setExtraInfoMsg(contactSalesMsg);
  } else {
    setErrorMsg(
      `Error creating team${
        errMsg != null ? ` (${errMsg})` : ''
      }. Please try again.`
    );
  }
  window.analytics?.track(trackingEventName, {
    organizationName: trackingOrgName,
    location: analyticsLocation,
    error: errMsg,
  });
}

type HandleGQLErrorsProps = {
  analyticsLocation: string;
  errors: GraphQLError[];
  errLabel: string;
  errExtra?: {[key: string]: any};
  trackingEventName: string;
  trackingOrgName: string | null;
  setErrorMsg: (m: string | null) => void;
};

function handleGQLErrors({
  analyticsLocation,
  errors,
  errLabel,
  errExtra,
  trackingEventName,
  trackingOrgName,
  setErrorMsg,
}: HandleGQLErrorsProps) {
  const collatedErrMsg = errors.map(e => e.message).join(', ');
  captureError(collatedErrMsg, errLabel, {
    extra: errExtra,
  });
  setErrorMsg('Error creating team');
  window.analytics?.track(trackingEventName, {
    organizationName: trackingOrgName,
    location: analyticsLocation,
    error: collatedErrMsg,
  });
}

type UseAvailabilityParams = {
  entityName: string | null;
  orgName: string | null;
  skip?: boolean;
};

export function useAvailability({
  entityName,
  orgName,
  skip,
}: UseAvailabilityParams): {
  loading: boolean;
  isEntityAvailable: boolean;
  isOrgAvailable: boolean;
} {
  // do not cache, gorilla currently has non-existent entities return
  // a static id, which seems to break apollo caching, possibly by making
  // it think all non-existent entities are the same
  const {loading: entityLoading, data: entityData} = useAvailableTeamQuery({
    variables: {
      teamName: entityName ?? '',
    },
    skip: !entityName || skip === true,
    fetchPolicy: 'no-cache',
  });
  const {loading: orgLoading, data: orgData} = useAvailableOrgQuery({
    variables: {
      name: orgName ?? '',
    },
    skip: !orgName || skip === true,
    fetchPolicy: 'no-cache',
    // https://github.com/apollographql/apollo-client/issues/11328 Fix for tests
    // which type quickly and cause a lack of re-renders when this query is complete
    // because the results are the same...
    notifyOnNetworkStatusChange: true,
  });

  return {
    loading: entityLoading || orgLoading,
    isEntityAvailable:
      !!entityName && !entityLoading && entityData?.entity?.available !== false,
    isOrgAvailable:
      !!orgName && !orgLoading && orgData?.organization?.available !== false,
  };
}
